/**
 * THE INVITE AND RECOVERY LINK, READ OFF THE URL
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS: THE TOKEN WAS BEING THROWN AWAY, SO NO INVITED USER
 * COULD EVER SET A PASSWORD
 * ─────────────────────────────────────────────────────────────────────────
 *
 * GoTrue answers `GET /verify?token=…&type=invite` with a 302 to
 * `redirect_to#access_token=…&refresh_token=…&expires_in=…&token_type=bearer&type=invite`
 * — the implicit flow, which is what supabase-js asks for by default
 * (`flowType: 'implicit'`, auth-js `GoTrueClient.js:21`).
 *
 * Nothing in this app read that fragment. `detectSessionInUrl` was false and no module
 * touched `location.hash`, so the browser dropped the only credential the invited person
 * had ever been issued and showed them a sign-in form for a password that had never been
 * set. The reset-password recipient had their recovery session discarded the same way.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY AN EXPLICIT HANDLER RATHER THAN `detectSessionInUrl: true`
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Turning the flag on establishes the session, and would fix half the defect. It cannot fix
 * the other half, for three measured reasons in auth-js 2.112.3:
 *
 *  1. IT DESTROYS `type`, WHICH IS THE BIT THAT DECIDES WHERE THE USER GOES. auth-js keeps
 *     the redirect type internally and surfaces only `recovery`, as a `PASSWORD_RECOVERY`
 *     event (`GoTrueClient.js:409`). An `invite` arrives as a bare `SIGNED_IN`, identical to
 *     someone typing their password — so the invited user lands on the console with no
 *     password of their own, which is the defect being fixed.
 *  2. IT CLEARS THE FRAGMENT BY NAVIGATING: `window.location.hash = ''`
 *     (`GoTrueClient.js:3316`). That pushes a history entry, so the previous entry still
 *     carries `#access_token=…` and the Back button puts a live bearer token back in the
 *     address bar. `history.replaceState` overwrites the entry instead, which is what
 *     `scrub()` below does.
 *  3. IT SWALLOWS THE FAILURE. `_initialize()` runs from the client constructor and "must
 *     never throw" (its own comment, `GoTrueClient.js:363`); an expired invite becomes a
 *     `_debug` line nobody sees. Read here, the same fragment can tell the recipient their
 *     link has expired and offer them the six-digit code instead.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE TOKEN IS NEVER LOGGED AND NEVER SENT ANYWHERE
 * ─────────────────────────────────────────────────────────────────────────
 *
 * What arrives in the fragment is a live bearer token for someone's account. It goes to
 * `supabase.auth.setSession()` and nowhere else: never into `console`, never into an error
 * string or a toast, never into telemetry or an API call, and never left in the address bar.
 * A fragment is not transmitted to a server by the browser — but it is copied by "copy
 * link", stored in history and saved in bookmarks, which is why it is scrubbed on arrival
 * rather than merely ignored.
 */

/** The `type` GoTrue puts in the fragment. The rest of the app cares about two of them. */
export type AuthLinkType = 'invite' | 'recovery' | 'magiclink' | 'signup' | 'email_change' | 'email';

const LINK_TYPES: readonly string[] = [
  'invite',
  'recovery',
  'magiclink',
  'signup',
  'email_change',
  'email',
];

export interface AuthLinkSession {
  kind: 'session';
  accessToken: string;
  refreshToken: string;
  /** Null when GoTrue sent a type this build does not know — the session is still good. */
  type: AuthLinkType | null;
}

export interface AuthLinkFailure {
  kind: 'failure';
  /** GoTrue's `error_code` where there is one, e.g. `otp_expired`. */
  code: string | null;
  /** Safe to render. See `describe()` for why the URL's own wording is not trusted. */
  message: string;
  type: AuthLinkType | null;
}

export type AuthLink = AuthLinkSession | AuthLinkFailure;

/**
 * A fragment longer than this is not something GoTrue produced.
 *
 * A JWT with a fat `user_metadata` claim runs to a couple of kilobytes, so the ceiling is
 * generous; it exists only so a pathological URL cannot make the regexes below quadratic.
 */
const MAX_LENGTH = 8192;

/*
 * An access token is a JWS: three base64url segments. Checking the shape is what stops a
 * hand-written `#access_token=whatever` from being handed to `setSession()` — GoTrue would
 * refuse it, but only after a round trip that leaves the app in a half-signed-in state.
 *
 * The refresh token is opaque, so the only honest check is the character set: GoTrue issues
 * it from a secure-random base64url encoder, and a value with a space, a quote or a control
 * character in it did not come from there.
 */
const JWT = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const OPAQUE_TOKEN = /^[A-Za-z0-9._-]{8,512}$/;

/** Keys that must appear at most once. A repeat is tampering, not a GoTrue redirect. */
const SINGLE_VALUED: readonly string[] = [
  'access_token',
  'refresh_token',
  'type',
  'error',
  'error_code',
  'error_description',
];

/**
 * Read a `location.hash` or `location.search` string as an auth link.
 *
 * Returns null when the string is not an auth callback at all — which includes the
 * `#reset`, `#signin` and `#resend` anchors the sign-in screen uses for its own links.
 *
 * Pure, and separated from the browser for exactly that reason: every case below is a URL
 * somebody can type, so each one is pinned in `auth-fragment.test.ts`.
 */
export function parseAuthLink(raw: string): AuthLink | null {
  if (!raw || raw.length > MAX_LENGTH) return null;

  /* Leading `#` or `?`, and the leading slash a hash-router style link carries. */
  const body = raw.replace(/^[#?]/, '').replace(/^\/+/, '');
  if (!body || !body.includes('=')) return null;

  const q = new URLSearchParams(body);

  const type = readType(q.get('type'));

  for (const key of SINGLE_VALUED) {
    if (q.getAll(key).length > 1) {
      /*
       * `URLSearchParams.get` returns the FIRST value, so a crafted
       * `#access_token=mine&access_token=theirs` would otherwise resolve to one of two
       * tokens depending on ordering. Neither is worth guessing between.
       */
      return { kind: 'failure', code: 'malformed_link', message: FALLBACK_FAILURE, type };
    }
  }

  const error = q.get('error');
  const errorCode = q.get('error_code');
  const errorDescription = q.get('error_description');
  if (error || errorCode || errorDescription) {
    return {
      kind: 'failure',
      code: errorCode ?? error ?? null,
      message: describe(errorCode ?? error, errorDescription),
      type,
    };
  }

  const accessToken = q.get('access_token');
  const refreshToken = q.get('refresh_token');

  /* Not an auth callback. Any other fragment on any other page lands here. */
  if (!accessToken && !refreshToken) return null;

  /*
   * Half a session is reported rather than ignored. `setSession` needs both halves, and a
   * recipient whose link arrived truncated — corporate gateways do rewrite and cut URLs —
   * must be shown the code fallback instead of a sign-in form that cannot help them.
   */
  if (!accessToken || !refreshToken) {
    return { kind: 'failure', code: 'incomplete_link', message: FALLBACK_FAILURE, type };
  }

  if (!JWT.test(accessToken) || !OPAQUE_TOKEN.test(refreshToken)) {
    return { kind: 'failure', code: 'malformed_link', message: FALLBACK_FAILURE, type };
  }

  return { kind: 'session', accessToken, refreshToken, type };
}

function readType(raw: string | null): AuthLinkType | null {
  if (!raw) return null;
  const t = raw.toLowerCase();
  return LINK_TYPES.includes(t) ? (t as AuthLinkType) : null;
}

const FALLBACK_FAILURE = 'That link did not work. Ask for a new one, or use the code from the email.';

/**
 * GoTrue's `error_description` is ATTACKER-CONTROLLED TEXT — it is whatever the URL says.
 *
 * React escapes it, so this is not an injection defence; it is a defence against putting
 * someone else's sentence in Thunder's voice on Thunder's sign-in screen. Known codes get
 * our own copy, and an unrecognised description is echoed only when it reads like prose.
 */
function describe(code: string | null, description: string | null): string {
  switch (code) {
    case 'otp_expired':
      return 'That link has expired. Request a new one from the sign-in screen, or ask your administrator to send another.';
    case 'access_denied':
      return 'That link is no longer valid. It may already have been used.';
    case 'over_email_send_rate_limit':
      return 'Too many emails have been requested for this address. Wait a minute and try again.';
    default:
      break;
  }
  if (description && /^[\w ,.'’()!:-]{1,200}$/.test(description)) {
    /* Sentence-cased and full-stopped, because GoTrue's is neither. */
    const s = description.trim();
    return /[.!?]$/.test(s) ? s : `${s}.`;
  }
  return FALLBACK_FAILURE;
}

/* ════════════════════════════════════════════════════════ the browser bit ══ */

/**
 * Captured at MODULE LOAD, before React renders anything.
 *
 * Not in an effect, and this is load-bearing: `AppInner`'s tab effect calls
 * `history.replaceState(…, '/overview')` with a path-only URL, which drops the fragment.
 * Anything that reads the hash later than module evaluation is racing that call for the
 * only copy of the token.
 */
const captured: AuthLink | null = capture();

function capture(): AuthLink | null {
  if (typeof window === 'undefined') return null;

  const fromHash = parseAuthLink(window.location.hash);

  /*
   * The query string is read for FAILURES ONLY. GoTrue reports some redirect errors there,
   * but it never puts tokens in a query string and neither will this app accept one: a
   * query string is sent to the server and lands in gateway and proxy access logs, so a
   * session offered that way is a session already written to someone's disk.
   */
  const fromQuery = parseAuthLink(window.location.search);
  const link = fromHash ?? (fromQuery?.kind === 'failure' ? fromQuery : null);

  /*
   * SCRUB WHATEVER LOOKS LIKE A CREDENTIAL, PARSED OR NOT.
   *
   * Scrubbing only on a successful parse left the one case that matters most unscrubbed: a
   * fragment over `MAX_LENGTH`, or one this module refuses for any other reason, may still
   * be carrying a real access token — and it would then sit in the address bar, in the
   * session history and in anything the operator copies, which is exactly what the header
   * of this file promises never happens. A refusal is a reason to remove it sooner, not
   * later.
   */
  if (link || looksLikeCredential(window.location.hash)) scrub();
  return link;
}

/**
 * Does this fragment carry something we must not leave lying about?
 *
 * Deliberately cruder than `parseAuthLink`: it answers "is there a credential here" rather
 * than "is this a link we accept", because those are different questions and only the first
 * one decides whether to scrub.
 */
function looksLikeCredential(raw: string): boolean {
  return /(?:^|[#?&])(?:access_token|refresh_token)=/.test(raw);
}

/**
 * Take the credential out of the address bar, keeping the path.
 *
 * `replaceState`, not `location.hash = ''`: the latter is a navigation and leaves the entry
 * that holds the token in the session history, one Back press away.
 */
function scrub(): void {
  try {
    const url = new URL(window.location.href);
    url.hash = '';
    for (const key of [...SINGLE_VALUED, 'expires_in', 'expires_at', 'token_type', 'provider_token', 'provider_refresh_token']) {
      url.searchParams.delete(key);
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}`);
  } catch {
    /*
     * `replaceState` throws in a sandboxed or `file://` document. The session still works;
     * the token stays visible in the bar until the next navigation, which is worse than
     * scrubbing and far better than refusing the invitation.
     */
  }
}

/**
 * The link this page was opened with, or null.
 *
 * Deliberately NOT consuming: React 19 calls a `useState` initialiser twice under
 * StrictMode, and a one-shot read would hand the second caller null and lose the routing
 * decision in development only — the worst possible place for that difference to live.
 * Redemption is made once by the guard in `auth.tsx` instead.
 */
export function readAuthLink(): AuthLink | null {
  return captured;
}
